// Learning & Development — react-query hooks over learningService (Programs +
// Sessions Phase 1). One query-key root ('learning') so every mutation can
// invalidate the slice it touched. Every failed mutation toasts the backend's
// own explanation via getApiErrorMessage (backend-error-surfacing rule).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import i18n from '@/i18n/config';
import * as api from '@/services/learningService';
import { getApiErrorMessage } from '@/lib/apiError';

export const learningKeys = {
  all: ['learning'] as const,
  journey: () => ['learning', 'me', 'journey'] as const,
  actions: () => ['learning', 'me', 'actions'] as const,
  mySessions: (from?: string, to?: string) => ['learning', 'me', 'sessions', from ?? '', to ?? ''] as const,
  myAssignments: () => ['learning', 'me', 'assignments'] as const,
  session: (id: string) => ['learning', 'session', id] as const,
  sessions: (from?: string, to?: string) => ['learning', 'sessions', from ?? '', to ?? ''] as const,
  programs: (status?: string) => ['learning', 'programs', status ?? ''] as const,
  program: (id: string) => ['learning', 'program', id] as const,
  programSessions: (id: string) => ['learning', 'program', id, 'sessions'] as const,
  participants: (sessionId: string) => ['learning', 'session', sessionId, 'participants'] as const,
  programTypes: () => ['learning', 'program-types'] as const,
  settings: () => ['learning', 'settings'] as const,
  audienceFacets: () => ['learning', 'audience', 'facets'] as const,
  programRule: (id: string) => ['learning', 'program', id, 'audience'] as const,
  sessionRule: (id: string) => ['learning', 'session', id, 'audience'] as const,
  tracks: (sessionId: string) => ['learning', 'session', sessionId, 'tracks'] as const,
  trackSummary: (sessionId: string) => ['learning', 'session', sessionId, 'track-summary'] as const,
  attendance: (sessionId: string) => ['learning', 'session', sessionId, 'attendance'] as const,
  champions: (kind: string, id: string) => ['learning', 'champions', kind, id] as const,
  // Phase 4
  completionPolicies: () => ['learning', 'completion-policies'] as const,
  practices: () => ['learning', 'practices'] as const,
  completion: (assignmentId: string) => ['learning', 'completion', assignmentId] as const,
  submissions: (sessionId?: string, status?: string) => ['learning', 'submissions', sessionId ?? '', status ?? ''] as const,
  systemEvents: () => ['learning', 'system-events'] as const,
  // Phase 5
  templates: () => ['learning', 'program-templates'] as const,
  template: (id: string) => ['learning', 'program-templates', id] as const,
  readiness: (programId: string) => ['learning', 'program', programId, 'readiness'] as const,
  myReadiness: () => ['learning', 'me', 'readiness'] as const,
};

const fail = (e: unknown) => toast.error(getApiErrorMessage(e, i18n.t('training:err', { defaultValue: 'Something went wrong' })));

// ---- reads ------------------------------------------------------------------
export const useMyJourney = (enabled = true) =>
  useQuery({ queryKey: learningKeys.journey(), queryFn: api.getMyJourney, enabled, staleTime: 60_000 });
export const useMyActions = (enabled = true) =>
  useQuery({ queryKey: learningKeys.actions(), queryFn: api.getMyActions, enabled, staleTime: 60_000 });
export const useMySessions = (from?: string, to?: string, enabled = true) =>
  useQuery({ queryKey: learningKeys.mySessions(from, to), queryFn: () => api.listMySessions(from, to), enabled, staleTime: 60_000 });
export const useMyAssignments = () =>
  useQuery({ queryKey: learningKeys.myAssignments(), queryFn: () => api.listAssignments(), staleTime: 30_000 });
export const useSession = (id: string | null | undefined) =>
  useQuery({ queryKey: learningKeys.session(id as string), queryFn: () => api.getSession(id as string), enabled: !!id });
export const useSessions = (from?: string, to?: string, enabled = true) =>
  useQuery({ queryKey: learningKeys.sessions(from, to), queryFn: () => api.listSessions(from, to), enabled, staleTime: 60_000 });
export const usePrograms = (status?: string, enabled = true) =>
  useQuery({ queryKey: learningKeys.programs(status), queryFn: () => api.listPrograms(status), enabled, staleTime: 30_000 });
export const useProgram = (id: string | null | undefined) =>
  useQuery({ queryKey: learningKeys.program(id as string), queryFn: () => api.getProgram(id as string), enabled: !!id });
export const useProgramSessions = (programId: string | null | undefined) =>
  useQuery({ queryKey: learningKeys.programSessions(programId as string), queryFn: () => api.listProgramSessions(programId as string), enabled: !!programId });
export const useParticipants = (sessionId: string | null | undefined, enabled = true) =>
  useQuery({ queryKey: learningKeys.participants(sessionId as string), queryFn: () => api.listParticipants(sessionId as string), enabled: !!sessionId && enabled });
export const useProgramTypes = (enabled = true) =>
  useQuery({ queryKey: learningKeys.programTypes(), queryFn: api.listProgramTypes, enabled, staleTime: 5 * 60_000 });
export const useLearningSettings = (enabled = true) =>
  useQuery({ queryKey: learningKeys.settings(), queryFn: api.getLearningSettings, enabled, staleTime: 5 * 60_000 });
/** Rule-builder vocabulary (facets + option lists + reachable people). */
export const useAudienceFacets = (enabled = true) =>
  useQuery({ queryKey: learningKeys.audienceFacets(), queryFn: api.getAudienceFacets, enabled, staleTime: 5 * 60_000 });
export const useProgramRule = (programId: string | null | undefined, enabled = true) =>
  useQuery({ queryKey: learningKeys.programRule(programId as string), queryFn: () => api.getProgramRule(programId as string), enabled: !!programId && enabled });
export const useSessionRule = (sessionId: string | null | undefined, enabled = true) =>
  useQuery({ queryKey: learningKeys.sessionRule(sessionId as string), queryFn: () => api.getSessionRule(sessionId as string), enabled: !!sessionId && enabled });

// Phase 3 reads: tracks (manage|assign), attendance (manage | trainer | champion | assign), champions (manage|assign).
export const useTracks = (sessionId: string | null | undefined, enabled = true) =>
  useQuery({ queryKey: learningKeys.tracks(sessionId as string), queryFn: () => api.listTracks(sessionId as string), enabled: !!sessionId && enabled });
export const useTrackSummary = (sessionId: string | null | undefined, enabled = true) =>
  useQuery({ queryKey: learningKeys.trackSummary(sessionId as string), queryFn: () => api.getTrackSummary(sessionId as string), enabled: !!sessionId && enabled, staleTime: 30_000 });
export const useAttendance = (sessionId: string | null | undefined, enabled = true) =>
  useQuery({ queryKey: learningKeys.attendance(sessionId as string), queryFn: () => api.listAttendance(sessionId as string), enabled: !!sessionId && enabled });
export const useChampions = (scopeKind: api.ChampionScopeKind, scopeId: string | null | undefined, enabled = true) =>
  useQuery({ queryKey: learningKeys.champions(scopeKind, scopeId as string), queryFn: () => api.listChampions(scopeKind, scopeId as string), enabled: !!scopeId && enabled, staleTime: 60_000 });

// ---- mutations --------------------------------------------------------------
function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: readonly (readonly unknown[])[]) => Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: k })));
}

export function useAudiencePreview() {
  return useMutation({ mutationFn: api.previewAudience, onError: fail });
}
export function useCreateProgramType() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: api.createProgramType, onSuccess: () => inv(learningKeys.programTypes()), onError: fail });
}
export function useCreateProgram() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: api.createProgram, onSuccess: () => inv(['learning', 'programs']), onError: fail });
}
export function useUpdateProgram() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<api.ProgramInput> }) => api.updateProgram(id, body),
    onSuccess: (_r, v) => inv(['learning', 'programs'], learningKeys.program(v.id)), onError: fail,
  });
}
export function usePublishProgram() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: api.publishProgram, onSuccess: () => inv(learningKeys.all), onError: fail });
}
export function useArchiveProgram() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: api.archiveProgram, onSuccess: () => inv(learningKeys.all), onError: fail });
}
export function useCreateSession() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ programId, body }: { programId: string; body: api.SessionInput }) => api.createSession(programId, body),
    onSuccess: (_r, v) => inv(learningKeys.programSessions(v.programId), ['learning', 'sessions'], ['learning', 'programs'], learningKeys.program(v.programId)),
    onError: fail,
  });
}
export function useUpdateSession() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<api.SessionInput> }) => api.updateSession(id, body),
    onSuccess: (s) => inv(learningKeys.session(s.id), learningKeys.programSessions(s.program_id), ['learning', 'sessions'], ['learning', 'me']),
    onError: fail,
  });
}
export function useCancelSession() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.cancelSession(id, reason),
    onSuccess: () => inv(learningKeys.all), onError: fail,
  });
}
export function useResolveSession() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.resolveSession(id),
    onSuccess: (_r, id) => inv(learningKeys.participants(id), learningKeys.session(id), ['learning', 'programs'], ['learning', 'program'], ['learning', 'sessions']),
    onError: fail,
  });
}
export function useIncludeParticipants() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body: { user_ids: string[]; assignment_type?: api.AssignmentType; note?: string } }) => api.includeParticipants(sessionId, body),
    onSuccess: (_r, v) => inv(learningKeys.participants(v.sessionId), learningKeys.session(v.sessionId), ['learning', 'program'], ['learning', 'programs'], ['learning', 'sessions']),
    onError: fail,
  });
}
export function useExcludeParticipant() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ sessionId, userId, reason }: { sessionId: string; userId: string; reason?: string }) => api.excludeParticipant(sessionId, userId, reason),
    onSuccess: (_r, v) => inv(learningKeys.participants(v.sessionId), learningKeys.session(v.sessionId), ['learning', 'program'], ['learning', 'programs'], ['learning', 'sessions']),
    onError: fail,
  });
}
/** Replace (or remove, with rule: null) a program's audience rule; the server re-resolves live sessions. */
export function usePutProgramRule() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ programId, body }: { programId: string; body: api.AudienceRuleInput }) => api.putProgramRule(programId, body),
    onSuccess: (_r, v) => inv(learningKeys.programRule(v.programId), learningKeys.program(v.programId), ['learning', 'programs'], ['learning', 'session'], ['learning', 'sessions'], ['learning', 'me']),
    onError: fail,
  });
}
/** Replace (or remove) one session's override rule. */
export function usePutSessionRule() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body: api.AudienceRuleInput }) => api.putSessionRule(sessionId, body),
    onSuccess: (_r, v) => inv(learningKeys.sessionRule(v.sessionId), learningKeys.session(v.sessionId), learningKeys.participants(v.sessionId), ['learning', 'program'], ['learning', 'programs'], ['learning', 'sessions'], ['learning', 'me']),
    onError: fail,
  });
}
export function useUpdateLearningSettings() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: api.updateLearningSettings, onSuccess: () => inv(learningKeys.settings()), onError: fail });
}
/**
 * Mark an assignment (course or session) complete. Phase 4: a policy-bound
 * assignment answers 400 with the unmet list — callers that want it inline pass
 * `{ quiet: true }` and render getApiErrorMessage themselves (no toast). `force`
 * is learning:manage only.
 */
export function useCompleteAssignment() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { evidence_url?: string; note?: string; score?: number; force?: boolean }; quiet?: boolean }) => api.completeAssignment(id, body),
    onSuccess: (_r, v) => inv(['learning', 'me'], ['learning', 'session'], ['learning', 'sessions'], learningKeys.completion(v.id), ['learning', 'submissions']),
    onError: (e, v) => { if (!v.quiet) fail(e); },
  });
}

// ---- Phase 3: tracks, attendance, champions, calendar mirror ----------------
/** Everything a track/attendance change can move: the session (me.track/attendance), roster, attendance, summary, my journey/sessions. */
const sessionSlice = (sessionId: string) => [
  learningKeys.session(sessionId), learningKeys.participants(sessionId), learningKeys.attendance(sessionId), learningKeys.tracks(sessionId),
  learningKeys.trackSummary(sessionId), ['learning', 'me'] as const, ['learning', 'sessions'] as const, ['learning', 'program'] as const,
];
export function useCreateTrack() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body: api.TrackInput }) => api.createTrack(sessionId, body),
    onSuccess: (_r, v) => inv(...sessionSlice(v.sessionId)), onError: fail,
  });
}
export function useUpdateTrack() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ trackId, body }: { trackId: string; sessionId: string; body: Partial<api.TrackInput> }) => api.updateTrack(trackId, body),
    onSuccess: (_r, v) => inv(...sessionSlice(v.sessionId)), onError: fail,
  });
}
export function useDeleteTrack() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ trackId }: { trackId: string; sessionId: string }) => api.deleteTrack(trackId),
    onSuccess: (_r, v) => inv(...sessionSlice(v.sessionId)), onError: fail,
  });
}
export function usePinParticipantTrack() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ sessionId, userId, trackId }: { sessionId: string; userId: string; trackId: string | null }) => api.pinParticipantTrack(sessionId, userId, trackId),
    onSuccess: (_r, v) => inv(...sessionSlice(v.sessionId)), onError: fail,
  });
}
export function useMarkAttendance() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ sessionId, marks }: { sessionId: string; marks: api.AttendanceMark[] }) => api.markAttendance(sessionId, marks),
    onSuccess: (_r, v) => inv(...sessionSlice(v.sessionId)), onError: fail,
  });
}
/** Confirm / check in to MY session. The server's 400 message explains a closed window; callers may also show it inline. */
export function useSelfAttendance() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ sessionId, action }: { sessionId: string; action: 'confirm' | 'checkin' }) => api.selfAttendance(sessionId, action),
    onSuccess: (_r, v) => inv(...sessionSlice(v.sessionId)), onError: fail,
  });
}
export function useAddChampion() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: api.ChampionInput) => api.addChampion(body),
    onSuccess: (_r, v) => inv(learningKeys.champions(v.scope_kind, v.scope_id), ['learning', 'session'], ['learning', 'sessions'], ['learning', 'me']), onError: fail,
  });
}
export function useRemoveChampion() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id }: { id: string; scopeKind: api.ChampionScopeKind; scopeId: string }) => api.removeChampion(id),
    onSuccess: (_r, v) => inv(learningKeys.champions(v.scopeKind, v.scopeId), ['learning', 'session'], ['learning', 'sessions'], ['learning', 'me']), onError: fail,
  });
}
export function useSyncSessionCalendar() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (sessionId: string) => api.syncSessionCalendar(sessionId),
    onSuccess: (_r, id) => inv(learningKeys.session(id), ['learning', 'sessions'], ['learning', 'program'], ['learning', 'me']), onError: fail,
  });
}

// ---- Phase 4: completion policies, practices, evidence, sign-offs ----------
// Reads: policies/practices (manage|assign), one assignment's completion view
// (owner | manage | assign-in-reach | trainer; 404 otherwise), the validator
// queue (manage | assign narrowed to reach).
export const useCompletionPolicies = (enabled = true) =>
  useQuery({ queryKey: learningKeys.completionPolicies(), queryFn: api.listCompletionPolicies, enabled, staleTime: 60_000 });
export const usePractices = (enabled = true) =>
  useQuery({ queryKey: learningKeys.practices(), queryFn: api.listPractices, enabled, staleTime: 60_000 });
export const useAssignmentCompletion = (assignmentId: string | null | undefined, enabled = true) =>
  useQuery({ queryKey: learningKeys.completion(assignmentId as string), queryFn: () => api.getAssignmentCompletion(assignmentId as string), enabled: !!assignmentId && enabled, retry: false });
export const useSubmissions = (params: { session_id?: string; status?: api.SubmissionStatus | '' } = {}, enabled = true) =>
  useQuery({ queryKey: learningKeys.submissions(params.session_id, params.status), queryFn: () => api.listSubmissions(params), enabled, staleTime: 15_000 });
/** Phase 6 catalog of platform events (manage|assign — callers pass `enabled` from their permission, so learners never hit the 403). */
export const useSystemEvents = (enabled = true) =>
  useQuery({ queryKey: learningKeys.systemEvents(), queryFn: api.getSystemEvents, enabled, staleTime: 5 * 60_000, retry: false });

/** Everything a completion change can move: the view itself, the session (me.status), roster, attendance (completion_state), queues, my journey/actions. */
const completionSlice = (assignmentId: string) => [
  learningKeys.completion(assignmentId), ['learning', 'submissions'] as const, ['learning', 'session'] as const, ['learning', 'sessions'] as const,
  ['learning', 'me'] as const, ['learning', 'program'] as const,
];
export function useCreateCompletionPolicy() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: api.createCompletionPolicy, onSuccess: () => inv(learningKeys.completionPolicies()), onError: fail });
}
export function useUpdateCompletionPolicy() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<api.CompletionPolicyInput> & { active?: boolean } }) => api.updateCompletionPolicy(id, body),
    onSuccess: () => inv(learningKeys.completionPolicies(), ['learning', 'completion']), onError: fail,
  });
}
export function useDeleteCompletionPolicy() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: (id: string) => api.deleteCompletionPolicy(id), onSuccess: () => inv(learningKeys.completionPolicies(), ['learning', 'completion']), onError: fail });
}
export function useCreatePractice() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: api.createPractice, onSuccess: () => inv(learningKeys.practices()), onError: fail });
}
export function useUpdatePractice() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<api.PracticeInput> }) => api.updatePractice(id, body),
    onSuccess: () => inv(learningKeys.practices(), ['learning', 'completion'], ['learning', 'submissions']), onError: fail,
  });
}
/** Record evidence / score on an assignment (owner or staff). Errors toast unless `quiet`. */
export function useSubmitEvidence() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ assignmentId, body }: { assignmentId: string; body: api.EvidenceInput; quiet?: boolean }) => api.submitEvidence(assignmentId, body),
    onSuccess: (_r, v) => inv(...completionSlice(v.assignmentId)), onError: (e, v) => { if (!v.quiet) fail(e); },
  });
}
/** Learner submits the practical exercise. The 400 for missing evidence/score is shown inline by PracticePanel (quiet). */
export function useSubmitPractice() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ assignmentId, body }: { assignmentId: string; body: api.EvidenceInput; quiet?: boolean }) => api.submitPractice(assignmentId, body),
    onSuccess: (_r, v) => inv(...completionSlice(v.assignmentId)), onError: (e, v) => { if (!v.quiet) fail(e); },
  });
}
export function useValidateSubmission() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ submissionId, body }: { submissionId: string; body: api.ValidateInput; assignmentId?: string }) => api.validateSubmission(submissionId, body),
    onSuccess: (r) => inv(...completionSlice(r.assignment_id)), onError: fail,
  });
}
export function useSignoffAssignment() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ assignmentId, body }: { assignmentId: string; body: api.SignoffInput }) => api.signoffAssignment(assignmentId, body),
    onSuccess: (_r, v) => inv(...completionSlice(v.assignmentId)), onError: fail,
  });
}

// ---- Phase 5: program templates, readiness ---------------------------------
// Reads: templates (manage|assign; tenant first, then platform, latest version
// each), one program's readiness (manage | assign narrowed to reach), my own
// readiness (self). Readiness state is rendered exactly as the server computed it.
export const useProgramTemplates = (enabled = true) =>
  useQuery({ queryKey: learningKeys.templates(), queryFn: api.listProgramTemplates, enabled, staleTime: 60_000 });
export const useProgramTemplate = (id: string | null | undefined, enabled = true) =>
  useQuery({ queryKey: learningKeys.template(id as string), queryFn: () => api.getProgramTemplate(id as string), enabled: !!id && enabled, staleTime: 60_000 });
export const useProgramReadiness = (programId: string | null | undefined, enabled = true) =>
  useQuery({ queryKey: learningKeys.readiness(programId as string), queryFn: () => api.getProgramReadiness(programId as string), enabled: !!programId && enabled, staleTime: 30_000 });
export const useMyReadiness = (enabled = true) =>
  useQuery({ queryKey: learningKeys.myReadiness(), queryFn: api.getMyReadiness, enabled, staleTime: 60_000 });

export function useCreateProgramTemplate() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: api.createProgramTemplate, onSuccess: () => inv(learningKeys.templates()), onError: fail });
}
/** POST /programs/from-template → a DRAFT program (sessions, tracks, practices, policies, readiness policy created). */
export function useCreateProgramFromTemplate() {
  const inv = useInvalidate();
  return useMutation({ mutationFn: api.createProgramFromTemplate, onSuccess: () => inv(['learning', 'programs'], ['learning', 'sessions']), onError: fail });
}
export function useCloneProgram() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: api.CloneProgramInput }) => api.cloneProgram(id, body),
    onSuccess: () => inv(['learning', 'programs'], ['learning', 'sessions']), onError: fail,
  });
}
export function useSaveProgramAsTemplate() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: api.SaveAsTemplateInput }) => api.saveProgramAsTemplate(id, body),
    onSuccess: () => inv(learningKeys.templates()), onError: fail,
  });
}
/** Replace (or remove, with null) a program's readiness policy; readiness and the journey are re-read. */
export function usePutReadinessPolicy() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ programId, policy }: { programId: string; policy: api.ReadinessPolicy | null }) => api.putReadinessPolicy(programId, policy),
    onSuccess: (_r, v) => inv(learningKeys.program(v.programId), ['learning', 'programs'], learningKeys.readiness(v.programId), ['learning', 'me']), onError: fail,
  });
}
export function useSetSessionCritical() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ sessionId, critical }: { sessionId: string; critical: boolean; programId?: string }) => api.setSessionCritical(sessionId, critical),
    onSuccess: (s, v) => inv(learningKeys.session(s.id), learningKeys.programSessions(s.program_id || v.programId || ''), ['learning', 'sessions'], learningKeys.readiness(s.program_id || v.programId || ''), ['learning', 'me']),
    onError: fail,
  });
}
